import React, { useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/AppShell";
import api from "@/lib/api";
import { UpgradeCard, parsePlanLockError } from "@/components/UpgradeCard";
import {
  Loader2, Building2, TrendingDown, ShieldCheck, Clock, Activity,
  ChevronUp, ChevronDown, Filter, AlertTriangle, ArrowUpRight, Flame,
} from "lucide-react";
import { toast } from "sonner";

const fmtPct = (n) => `${(n ?? 0).toFixed(1)}%`;
const fmtMoney = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", notation: "compact", maximumFractionDigits: 1 }).format(n || 0);
const fmtNum = (n) => new Intl.NumberFormat("en-US").format(n || 0);

// Per-metric grade -> contextual indicator (replaces "Behind / Ahead").
// `kind` controls which semantic palette renders.
const GRADE_KINDS = {
  ahead:    { kind: "favorable",       icon: ChevronDown, label: "Favorable" },
  behind:   { kind: "unfavorable",     icon: ChevronUp,   label: "Unfavorable" },
  on_track: { kind: "informational",   icon: ChevronUp,   label: "At Benchmark" },
};
// Severity override for behind-status preventable metric (high risk leakage).
const PALETTE = {
  favorable:     { bg: "rgba(74,222,128,0.16)", border: "rgba(74,222,128,0.55)", color: "#86EFAC" },
  unfavorable:   { bg: "rgba(251,191,36,0.15)", border: "rgba(251,191,36,0.55)", color: "#FCD34D" },
  high_risk:     { bg: "rgba(248,113,113,0.18)", border: "rgba(248,113,113,0.60)", color: "#FCA5A5" },
  critical:      { bg: "rgba(220,38,38,0.22)",  border: "rgba(220,38,38,0.70)",  color: "#FCA5A5" },
  needs_attention:{bg: "rgba(251,191,36,0.15)", border: "rgba(251,191,36,0.55)", color: "#FCD34D" },
  informational: { bg: "rgba(94,234,212,0.14)", border: "rgba(94,234,212,0.45)", color: "#5EEAD4" },
};

// Per-metric label semantics (denial rate "behind" = unfavorable; preventable "behind" = high risk).
function gradeLabel(metric, grade) {
  if (!grade || grade.status === "n/a") return null;
  if (grade.status === "on_track") return { kind: "informational", icon: ChevronUp, text: "At Benchmark" };
  if (grade.status === "ahead") {
    // Lower is better for these metrics
    if (metric === "denial_rate" || metric === "preventable_pct" || metric === "avg_aging_days") {
      return { kind: "favorable", icon: ChevronDown, text: "Favorable" };
    }
    // Higher is better for first_pass_estimate
    return { kind: "favorable", icon: ChevronUp, text: "Favorable" };
  }
  // status === "behind"
  if (metric === "preventable_pct") {
    return { kind: "high_risk", icon: AlertTriangle, text: "High Risk" };
  }
  if (metric === "first_pass_estimate") {
    return { kind: "critical", icon: AlertTriangle, text: "Critical" };
  }
  if (metric === "denial_rate") {
    return { kind: "unfavorable", icon: ChevronUp, text: "Unfavorable" };
  }
  return { kind: "needs_attention", icon: ChevronUp, text: "Needs Attention" };
}

const ContextPill = ({ metric, grade, testId }) => {
  const meta = gradeLabel(metric, grade);
  if (!meta) return <span className="text-slate-500">—</span>;
  const Icon = meta.icon;
  const p = PALETTE[meta.kind] || PALETTE.informational;
  return (
    <span
      data-testid={testId}
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider"
      style={{ background: p.bg, border: `1px solid ${p.border}`, color: p.color }}
      title={grade?.label}
    >
      <Icon className="h-3 w-3" />
      {meta.text}
    </span>
  );
};

const SEVERITY_META = {
  critical:        { kind: "critical",       icon: Flame, label: "Critical" },
  high_risk:       { kind: "high_risk",      icon: AlertTriangle, label: "High Risk" },
  needs_attention: { kind: "needs_attention",icon: ArrowUpRight, label: "Opportunity" },
  favorable:       { kind: "favorable",      icon: ShieldCheck, label: "Favorable" },
};
const SeverityBadge = ({ severity, testId }) => {
  const m = SEVERITY_META[severity] || SEVERITY_META.needs_attention;
  const p = PALETTE[m.kind];
  const Icon = m.icon;
  return (
    <span
      data-testid={testId}
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-bold uppercase tracking-wider"
      style={{ background: p.bg, border: `1px solid ${p.border}`, color: p.color }}
    >
      <Icon className="h-3.5 w-3.5" />
      {m.label}
    </span>
  );
};

const SEVERITY_FILTERS = [
  { id: "all", label: "All Payors" },
  { id: "critical", label: "Critical" },
  { id: "high_risk", label: "High Risk" },
  { id: "needs_attention", label: "Opportunities" },
  { id: "favorable", label: "Favorable" },
];

const SORT_OPTIONS = [
  { id: "severity_score", label: "Risk Severity", dir: "desc" },
  { id: "revenue_at_risk", label: "Revenue at Risk", dir: "desc" },
  { id: "recoverable_dollars", label: "Recoverable $", dir: "desc" },
  { id: "denial_rate", label: "Denial Rate", dir: "desc" },
  { id: "first_pass_estimate", label: "First Pass %", dir: "asc" },
  { id: "avg_aging_days", label: "Avg AR Days", dir: "desc" },
  { id: "payor", label: "Payor (A–Z)", dir: "asc" },
];

export default function ScorecardsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [planLock, setPlanLock] = useState(null);
  const [severityFilter, setSeverityFilter] = useState("all");
  const [sortBy, setSortBy] = useState("severity_score");
  const [sortDir, setSortDir] = useState("desc");

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/scorecards/payors");
      setData(data);
      setPlanLock(null);
    } catch (err) {
      const lock = parsePlanLockError(err);
      if (lock) {
        setPlanLock(lock);
      } else {
        toast.error(err?.response?.data?.detail || "No active dataset. Upload data or load demo first.");
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  // ---- Derived rows: filter + sort ----
  const rows = useMemo(() => {
    if (!data?.payors) return [];
    let r = data.payors.slice();
    if (severityFilter !== "all") r = r.filter((p) => p.severity === severityFilter);
    const getVal = (p, key) => {
      switch (key) {
        case "severity_score":    return p.severity_score ?? 0;
        case "revenue_at_risk":   return p.revenue_at_risk ?? 0;
        case "recoverable_dollars": return p.recoverable_dollars ?? 0;
        case "denial_rate":       return p.kpis.denial_rate ?? 0;
        case "first_pass_estimate": return p.kpis.first_pass_estimate ?? 0;
        case "avg_aging_days":    return p.kpis.avg_aging_days ?? 0;
        case "payor":             return p.payor || "";
        default: return 0;
      }
    };
    r.sort((a, b) => {
      const av = getVal(a, sortBy);
      const bv = getVal(b, sortBy);
      if (typeof av === "string") return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortDir === "asc" ? av - bv : bv - av;
    });
    return r;
  }, [data, severityFilter, sortBy, sortDir]);

  const counts = useMemo(() => {
    const c = { critical: 0, high_risk: 0, needs_attention: 0, favorable: 0 };
    (data?.payors || []).forEach((p) => { c[p.severity] = (c[p.severity] || 0) + 1; });
    return c;
  }, [data]);

  const toggleSort = (id) => {
    if (sortBy === id) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortBy(id);
      const def = SORT_OPTIONS.find((o) => o.id === id)?.dir || "desc";
      setSortDir(def);
    }
  };

  if (loading) {
    return (
      <AppShell>
        <div className="flex flex-col items-center justify-center py-32 text-slate-300">
          <Loader2 className="h-10 w-10 animate-spin text-teal-400" />
          <div className="mt-4 text-sm">Loading payor scorecards...</div>
        </div>
      </AppShell>
    );
  }

  if (planLock) {
    return (
      <AppShell>
        <div className="max-w-3xl mx-auto py-8" data-testid="scorecards-plan-lock">
          <UpgradeCard
            feature={planLock.feature}
            featureLabel={planLock.featureLabel}
            description="Per-payor scorecards grade every insurer on denial rate, revenue at risk, recoverable dollars, and risk severity. Build payor scorecards your CFO can use in contract negotiations."
            requiredPlan={planLock.requiredPlan}
            currentPlan={planLock.currentPlan}
          />
        </div>
      </AppShell>
    );
  }

  if (!data?.payors?.length) {
    return (
      <AppShell>
        <div className="max-w-2xl mx-auto text-center py-20">
          <h2 className="font-display text-2xl font-semibold text-slate-100">No data available</h2>
          <p className="text-slate-400 mt-2">Upload a dataset or load demo data to see payor scorecards.</p>
        </div>
      </AppShell>
    );
  }

  const b = data.benchmarks;
  // eslint-disable-next-line react/no-unstable-nested-components
  const Th = ({ id, children, align = "right" }) => {
    const active = sortBy === id;
    return (
      <th
        className={`py-3 px-3 font-semibold text-[11px] uppercase tracking-wider cursor-pointer select-none whitespace-nowrap ${
          align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left"
        } ${active ? "text-teal-300" : "text-slate-300 hover:text-slate-100"}`}
        onClick={() => toggleSort(id)}
        data-testid={`scorecard-sort-${id}`}
      >
        <span className="inline-flex items-center gap-1">
          {children}
          {active && (sortDir === "asc" ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />)}
        </span>
      </th>
    );
  };

  return (
    <AppShell>
      <div className="space-y-6 fade-up" data-testid="scorecards-page">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
          <div>
            <span className="label-overline">Executive Payor Performance</span>
            <h1 className="font-display text-3xl sm:text-4xl font-semibold text-slate-100 mt-1.5 tracking-tight">
              Payor Performance Intelligence
            </h1>
            <p className="text-sm text-slate-300 mt-1.5 max-w-3xl">
              Each payor ranked by operational severity and revenue at risk, dynamically benchmarked against{" "}
              <span className="font-semibold text-teal-300 capitalize">{data.vertical}</span> industry medians
              (denial rate {b.denial_rate.median}%, preventable {b.preventable_pct.median}%, first-pass{" "}
              {b.first_pass_estimate.median}%, avg AR {b.avg_aging_days.median}d).
            </p>
          </div>
          {/* Severity rollup chips */}
          <div className="flex flex-wrap items-center gap-1.5">
            {counts.critical > 0 && <SeverityBadge severity="critical" testId="severity-rollup-critical" />}
            {counts.high_risk > 0 && <SeverityBadge severity="high_risk" testId="severity-rollup-high_risk" />}
            {counts.needs_attention > 0 && <SeverityBadge severity="needs_attention" testId="severity-rollup-needs_attention" />}
            {counts.favorable > 0 && <SeverityBadge severity="favorable" testId="severity-rollup-favorable" />}
          </div>
        </div>

        {/* Filters */}
        <div className="surface-card p-3 flex flex-wrap items-center gap-2" data-testid="scorecard-filter-bar">
          <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold text-slate-400 px-2">
            <Filter className="h-3 w-3" /> Filter
          </div>
          {SEVERITY_FILTERS.map((f) => (
            <button
              key={f.id}
              data-testid={`scorecard-filter-${f.id}`}
              onClick={() => setSeverityFilter(f.id)}
              className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${
                severityFilter === f.id
                  ? "bg-teal-400/15 border-teal-400/60 text-teal-200"
                  : "border-white/10 text-slate-300 hover:text-white hover:border-white/30"
              }`}
            >
              {f.label}
              {f.id !== "all" && counts[f.id] !== undefined && (
                <span className="ml-1.5 text-slate-500">{counts[f.id]}</span>
              )}
            </button>
          ))}
          <div className="ml-auto text-[11px] text-slate-400">
            {rows.length} of {data.payors.length} payors
          </div>
        </div>

        {/* Scorecard table */}
        <div className="surface-card overflow-hidden" data-testid="payor-scorecard-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/5">
                <tr className="border-b border-white/10">
                  <Th id="payor" align="left">Payor</Th>
                  <Th id="severity_score" align="left">Severity</Th>
                  <Th id="revenue_at_risk">Revenue at Risk</Th>
                  <Th id="recoverable_dollars">Recoverable</Th>
                  <th className="py-3 px-3 font-semibold text-[11px] uppercase tracking-wider text-slate-300 text-left">Primary Driver</th>
                  <Th id="denial_rate" align="center">Denial Rate</Th>
                  <th className="py-3 px-3 font-semibold text-[11px] uppercase tracking-wider text-slate-300 text-center">Preventable</th>
                  <Th id="first_pass_estimate" align="center">First-Pass</Th>
                  <Th id="avg_aging_days" align="center">AR Days</Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((p, i) => (
                  <tr
                    key={p.payor}
                    data-testid={`scorecard-row-${i}`}
                    className="border-b border-white/5 hover:bg-white/[0.03]"
                  >
                    <td className="py-3.5 px-5">
                      <div className="flex items-center gap-2.5">
                        <div
                          className="h-8 w-8 rounded-md flex items-center justify-center"
                          style={{ background: "rgba(20,184,166,0.15)", border: "1px solid rgba(20,184,166,0.30)" }}
                        >
                          <Building2 className="h-4 w-4 text-teal-300" />
                        </div>
                        <span className="font-semibold text-slate-100">{p.payor}</span>
                      </div>
                    </td>
                    <td className="py-3.5 px-3">
                      <SeverityBadge severity={p.severity} testId={`scorecard-severity-${i}`} />
                      <div className="text-[10px] text-slate-500 mt-1">Score {p.severity_score}</div>
                    </td>
                    <td className="py-3.5 px-3 text-right">
                      <div className="font-mono font-semibold text-slate-100">{fmtMoney(p.revenue_at_risk)}</div>
                      <div className="text-[10px] text-slate-500">{fmtNum(p.kpis.volume)} claims</div>
                    </td>
                    <td className="py-3.5 px-3 text-right">
                      <div className="font-mono font-semibold" style={{ color: "#5EEAD4" }}>
                        {fmtMoney(p.recoverable_dollars)}
                      </div>
                      <div className="text-[10px] text-slate-500">
                        {fmtPct(p.recoverable_dollars / Math.max(p.revenue_at_risk, 1) * 100)} of risk
                      </div>
                    </td>
                    <td className="py-3.5 px-3 text-slate-200">
                      <div className="text-[13px]" title={p.primary_driver}>{p.primary_driver}</div>
                      {p.secondary_driver && (
                        <div className="text-[10px] text-slate-500 mt-0.5">+ {p.secondary_driver}</div>
                      )}
                    </td>
                    <td className="py-3.5 px-3 text-center">
                      <div className="flex flex-col items-center gap-1">
                        <span className="font-mono text-slate-100">{fmtPct(p.kpis.denial_rate)}</span>
                        <ContextPill metric="denial_rate" grade={p.grades.denial_rate} testId={`scorecard-grade-denial-${i}`} />
                      </div>
                    </td>
                    <td className="py-3.5 px-3 text-center">
                      <div className="flex flex-col items-center gap-1">
                        <span className="font-mono text-slate-100">{fmtPct(p.kpis.preventable_pct)}</span>
                        <ContextPill metric="preventable_pct" grade={p.grades.preventable_pct} testId={`scorecard-grade-preventable-${i}`} />
                      </div>
                    </td>
                    <td className="py-3.5 px-3 text-center">
                      <div className="flex flex-col items-center gap-1">
                        <span className="font-mono text-slate-100">{fmtPct(p.kpis.first_pass_estimate)}</span>
                        <ContextPill metric="first_pass_estimate" grade={p.grades.first_pass_estimate} testId={`scorecard-grade-firstpass-${i}`} />
                      </div>
                    </td>
                    <td className="py-3.5 px-3 text-center">
                      <div className="flex flex-col items-center gap-1">
                        <span className="font-mono text-slate-100">{p.kpis.avg_aging_days}d</span>
                        <ContextPill metric="avg_aging_days" grade={p.grades.avg_aging_days} testId={`scorecard-grade-aging-${i}`} />
                      </div>
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr><td colSpan={9} className="py-12 text-center text-slate-500 text-sm">No payors match this filter.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Industry medians tiles */}
        <div className="grid sm:grid-cols-4 gap-3">
          {[
            { icon: TrendingDown, label: "Denial Rate", v: b.denial_rate.median, unit: "%" },
            { icon: ShieldCheck, label: "Preventable", v: b.preventable_pct.median, unit: "%" },
            { icon: Activity, label: "First-Pass", v: b.first_pass_estimate.median, unit: "%" },
            { icon: Clock, label: "Avg AR Days", v: b.avg_aging_days.median, unit: "d" },
          ].map((m, i) => (
            <div key={i} className="surface-card p-4" data-testid={`benchmark-tile-${i}`}>
              <div className="flex items-center gap-2">
                <div
                  className="h-7 w-7 rounded-md flex items-center justify-center"
                  style={{ background: "rgba(20,184,166,0.15)", border: "1px solid rgba(20,184,166,0.30)" }}
                >
                  <m.icon className="h-3.5 w-3.5 text-teal-300" />
                </div>
                <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">{m.label}</div>
              </div>
              <div className="font-display text-2xl font-semibold text-slate-100 mt-2.5">{m.v}{m.unit}</div>
              <div className="text-xs text-slate-400">Industry median</div>
            </div>
          ))}
        </div>
      </div>
    </AppShell>
  );
}
